/* eslint-disable react/prop-types */
import useTranslation from '@/hooks/i18n';
import { useNavigate } from 'react-router-dom';
import AnimatedFadeInComponent from '../AnimatedFadeInComponent';

export interface Tab {
  name: string;
  href?: string;
  onClick?: (event?: React.MouseEvent<HTMLAnchorElement, MouseEvent>, tab?: Tab) => void;
  current: boolean;
}

function classNames(...classes: string[]) {
  return classes.filter(Boolean).join(' ');
}

function capitalizeFirstLetter(string?: string) {
  if (!string) return '';
  return string?.charAt(0)?.toUpperCase() + string?.slice(1);
}

export default function Tabs({
  tabs,
  setTabs,
  enableEmptyOnClick = false,
  selectClassNames,
  children,
  tabsClassesNames,
  textClassNames,
}: {
  tabs: Tab[];
  setTabs?: React.Dispatch<Tab[]>;
  enableEmptyOnClick?: boolean;
  selectClassNames?: string;
  children?: React.ReactNode;
  tabsClassesNames?: string;
  textClassNames?: string;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  function onSelectTab(event: React.ChangeEvent<HTMLSelectElement>, tab: Tab) {
    event?.preventDefault();

    if (tab.href) {
      navigate(tab.href);
      return;
    }

    if (setTabs) {
      setTabs([
        ...tabs.map((t) => {
          if (t.name === tab?.name) {
            return { ...t, current: true };
          } else {
            return { ...t, current: false };
          }
        }),
      ]);
    }
    if (tab?.onClick && enableEmptyOnClick) {
      tab?.onClick();
    }
  }
  function onClickTab(event: React.MouseEvent<HTMLAnchorElement, MouseEvent>, tab: Tab) {
    event?.preventDefault();

    if (tab.onClick) {
      tab.onClick(event, tab);
    }

    if (tab.href) {
      navigate(tab.href);
      return;
    }
    if (setTabs) {
      setTabs([
        ...tabs.map((t) => {
          if (t.name === tab?.name) {
            return { ...t, current: true };
          } else {
            return { ...t, current: false };
          }
        }),
      ]);
    }
  }

  return (
    <div>
      <div className={`mt-10 lg:hidden ${selectClassNames}`}>
        <label htmlFor="tabs" className="sr-only">
          Select a tab
        </label>
        <select
          id="tabs"
          name="tabs"
          onChange={(e) => {
            onSelectTab(e, tabs.find((tab) => t(tab?.name) === e.target.value)!);
          }}
          className="block w-full rounded-md border-gray-300 py-2 pr-10 text-base focus:outline-none focus:ring-indigo-500 sm:text-sm"
          defaultValue={tabs.find((tab) => tab?.current)?.name}
          value={t(tabs.find((tab) => tab?.current)?.name || '')}>
          {tabs.map((tab) => (
            <option key={tab?.name}>{t(tab?.name)}</option>
          ))}
        </select>
      </div>
      <div>
        <AnimatedFadeInComponent>
          <div className="hidden border-gray-200 lg:block">
            <nav
              className={classNames(
                'inline-flex h-10 w-full items-center justify-center gap-8 rounded-md text-sh-xxs font-bold',
                tabsClassesNames || '',
              )}
              aria-label="Tabs">
              {tabs.map((tab) => (
                <>
                  <a
                    key={tab?.name}
                    href={tab.href}
                    onClick={(e) => onClickTab(e, tab)}
                    className={classNames(
                      tab.current
                        ? ' border-blue-500 text-foreground shadow-sm'
                        : 'border-transparent text-black-100 hover:border-blue-500 hover:text-blue-500',
                      'cursor-pointer whitespace-nowrap border-b-[2px] py-1 text-sh-xxs font-bold',
                      textClassNames || '',
                    )}
                    aria-current={tab.current ? 'page' : undefined}>
                    {capitalizeFirstLetter(t(tab?.name?.toLowerCase()))}
                  </a>
                </>
              ))}
            </nav>
          </div>
        </AnimatedFadeInComponent>
        <div className="mt-8">{children}</div>
      </div>
    </div>
  );
}
